import { useState, useEffect } from 'react';
import { X, Loader2 } from 'lucide-react';
import { creditLimitsApi } from '../../services/creditLimitsApi';
import type { DrillDownType } from '../../types/creditLimits';

interface TransactionDrillDownModalProps {
    customerCode: string;
    customerName: string;
    drillDownType: DrillDownType;
    onClose: () => void;
}

function formatNum(value: number | null | undefined, decimals = 0): string {
    if (value == null) return '-';
    return new Intl.NumberFormat('de-CH', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    }).format(value);
}

function formatDate(value: string | null | undefined): string {
    if (!value) return '-';
    return new Date(value).toLocaleDateString('de-CH');
}

// Map BC document type values (numeric codes from etl tables, German labels
// from native BC tables, English labels from etl.OpenSalesOrders) to a
// consistent English display label.
function translateDocumentType(value: unknown): string {
    if (typeof value === 'number') {
        switch (value) {
            case 1: return 'Payment';
            case 2: return 'Invoice';
            case 3: return 'Credit Memo';
            case 4: return 'Refund';
            case 5: return 'Reminder';
            default: return String(value);
        }
    }
    const s = String(value ?? '').trim();
    switch (s) {
        case '2': return 'Invoice';
        case '3': return 'Credit Memo';
        case 'Rechnung': return 'Invoice';
        case 'Gutschrift': return 'Credit Memo';
        case 'Zahlung': return 'Payment';
        case 'Erstattung': return 'Refund';
        case 'Order': return 'Order';
        case 'Frame': return 'Frame Order';
        case 'Consigment': return 'Consignment';
        default: return s;
    }
}

// Open invoices live in the overview's own dialog (getOpenInvoices), not here.
const TITLES: Record<DrillDownType, string> = {
    salesTransactions: 'Net Sales (12 Months)',
    openOrders: 'Open Orders',
    openSalesOrders: 'Open Sales Orders',
    openFrameOrders: 'Open Frame Orders',
};

const CURRENCY_HINTS: Record<DrillDownType, string> = {
    salesTransactions: 'Values in CHF (local currency) — totals match the main list.',
    openOrders: 'Values in EUR — main list shown in CHF (FX-converted at daily rate).',
    openSalesOrders: 'Values in EUR — main list shown in CHF (FX-converted at daily rate).',
    openFrameOrders: 'Values in EUR — main list shown in CHF (FX-converted at daily rate).',
};

const isOpenOrdersMode = (type: DrillDownType): boolean =>
    type === 'openOrders' || type === 'openSalesOrders' || type === 'openFrameOrders';

export function TransactionDrillDownModal({ customerCode, customerName, drillDownType, onClose }: TransactionDrillDownModalProps) {
    const [data, setData] = useState<Record<string, unknown>[] | null>(null);
    const [totals, setTotals] = useState<Record<string, unknown> | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setLoading(true);
        setError(null);

        const fetchData = async () => {
            try {
                if (drillDownType === 'salesTransactions') {
                    const res = await creditLimitsApi.getSalesTransactions(customerCode);
                    setData(res.transactions as unknown as Record<string, unknown>[]);
                    setTotals(res.totals as unknown as Record<string, unknown>);
                } else if (isOpenOrdersMode(drillDownType)) {
                    const typeFilter = drillDownType === 'openSalesOrders' ? 'sales'
                        : drillDownType === 'openFrameOrders' ? 'frame'
                        : undefined;
                    const res = await creditLimitsApi.getOpenOrders(customerCode, typeFilter);
                    setData(res.orders as unknown as Record<string, unknown>[]);
                    setTotals(res.totals as unknown as Record<string, unknown>);
                }
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to load data');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [customerCode, drillDownType]);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
            <div
                className="bg-white rounded-lg shadow-xl w-[90vw] max-w-5xl max-h-[80vh] flex flex-col"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">{TITLES[drillDownType]}</h3>
                        <p className="text-sm text-gray-500">{customerName} ({customerCode})</p>
                        {CURRENCY_HINTS[drillDownType] && (
                            <p className="mt-1 text-xs text-amber-700 italic">{CURRENCY_HINTS[drillDownType]}</p>
                        )}
                    </div>
                    <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-auto p-6">
                    {loading && (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
                            <span className="ml-2 text-gray-500">Loading transactions...</span>
                        </div>
                    )}
                    {error && <p className="text-red-600 text-center py-12">{error}</p>}

                    {!loading && !error && data && (
                        <>
                            {/* Totals summary */}
                            {totals && (
                                <div className="mb-4 flex gap-4 text-sm">
                                    {Object.entries(totals).map(([key, val]) => (
                                        <div key={key} className="px-3 py-2 rounded bg-gray-50 border border-gray-200">
                                            <span className="text-xs text-gray-500">{key.replace(/([A-Z])/g, ' $1').trim()}: </span>
                                            <span className="font-medium">{typeof val === 'number' ? formatNum(val, 2) : String(val)}</span>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {data.length === 0 ? (
                                <p className="text-gray-500 text-center py-8">No records found.</p>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                {drillDownType === 'salesTransactions' && (
                                                    <>
                                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Document</th>
                                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Type</th>
                                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Date</th>
                                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Description</th>
                                                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Net Sales (CHF)</th>
                                                    </>
                                                )}
                                                {isOpenOrdersMode(drillDownType) && (
                                                    <>
                                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Order</th>
                                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Type</th>
                                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Created</th>
                                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Delivery</th>
                                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Product</th>
                                                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Qty</th>
                                                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Amount (EUR)</th>
                                                    </>
                                                )}
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200">
                                            {data.map((row, i) => (
                                                <tr key={i} className="hover:bg-gray-50">
                                                    {drillDownType === 'salesTransactions' && (
                                                        <>
                                                            <td className="px-3 py-1.5">{String(row.DocumentNo || '')}</td>
                                                            <td className="px-3 py-1.5">{translateDocumentType(row.DocumentType)}</td>
                                                            <td className="px-3 py-1.5">{formatDate(row.PostingDate as string)}</td>
                                                            <td className="px-3 py-1.5 max-w-md truncate" title={String(row.Description || '')}>
                                                                {String(row.Description || '')}
                                                            </td>
                                                            <td className={`px-3 py-1.5 text-right tabular-nums font-medium ${
                                                                typeof row.NetSalesLCY === 'number' && row.NetSalesLCY < 0 ? 'text-red-600' : ''
                                                            }`}>
                                                                {formatNum(row.NetSalesLCY as number, 2)}
                                                            </td>
                                                        </>
                                                    )}
                                                    {isOpenOrdersMode(drillDownType) && (
                                                        <>
                                                            <td className="px-3 py-1.5">{String(row.DocumentCode || '')}</td>
                                                            <td className="px-3 py-1.5">{translateDocumentType(row.DocumentType)}</td>
                                                            <td className="px-3 py-1.5">{formatDate(row.CreateDate as string)}</td>
                                                            <td className="px-3 py-1.5">{formatDate(row.ExpectedDeliveryDate as string)}</td>
                                                            <td className="px-3 py-1.5">
                                                                <span title={String(row.ProductID || '')}>{String(row.ProductName || row.ProductID || '')}</span>
                                                            </td>
                                                            <td className="px-3 py-1.5 text-right tabular-nums">{formatNum(row.OutstandingQuantity as number, 2)}</td>
                                                            <td className="px-3 py-1.5 text-right tabular-nums font-medium">{formatNum(row.OutstandingNetSalesEUR as number, 2)}</td>
                                                        </>
                                                    )}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
